<template>
    <Modal @close="cancel">
        <div class="ds-ex-dialog">
            <div class="ds-glass-dialog-title">{{ isRequest ? 'Удалить запрос?' : 'Снять вакансию?' }}</div>
            <div class="ds-glass-dialog-text">{{ subject }}</div>
            <label class="ds-ex-dialog-field">
                <span class="ds-glass-label">Причина{{ reasonRequired ? '' : ' (необязательно)' }}</span>
                <textarea v-model="reason" class="ds-ex-field ds-ex-textarea" maxlength="100" rows="3" :disabled="saving"></textarea>
                <span v-if="reasonRequired" class="ds-ex-muted">Создана больше 15 минут назад — причина обязательна.</span>
                <span v-if="error" class="ds-ex-error" role="alert">{{ error }}</span>
            </label>
            <div class="ds-glass-dialog-actions">
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="saving" @click="cancel">Отмена</button>
                <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="saving || (reasonRequired && !reason.trim())" @click="confirm">
                    {{ saving ? 'Снимаем…' : isRequest ? 'Удалить' : 'Снять' }}
                </button>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import Modal from '@/ui/Modal.vue'
import { exchangeApi } from './exchangeApi'
import { errorText, useExchangeGuard } from './exchangeActions'
import { exchangeToast } from './exchangeToasts'
import { dayKeyOf, rowDateLabel, timeRange } from './exchangeFormat'
import type { ExchangeError, Vacancy } from './exchangeTypes'

const props = defineProps<{
    vacancy: Vacancy
    reasonDemanded?: boolean
    // Обновление после снятия — внутри общей блокировки вакансии.
    afterRemoved: (id: string) => Promise<void>
}>()
const emit = defineEmits<{ close: [] }>()
const { isBusy, guarded } = useExchangeGuard()

const REASON_AFTER = 15 * 60_000

const reason = ref('')
const saving = ref(false)
const error = ref('')
// Подсказка по created_at; окончательно порог проверяет сервер (409 deletion_reason_required).
const serverRequired = ref(Boolean(props.reasonDemanded))

const isRequest = computed(() => props.vacancy.isRequest)
// Диалог открывается только когда причина нужна: создана > 15 минут назад, нет created_at или её потребовал сервер.
const reasonRequired = computed(() => serverRequired.value || !props.vacancy.createdAt || Date.now() - dayjs(props.vacancy.createdAt).valueOf() > REASON_AFTER)
const subject = computed(() => `${props.vacancy.position.name} · ${rowDateLabel(dayKeyOf(props.vacancy.start))} · ${timeRange(props.vacancy)}`)

function cancel() {
    if (!saving.value) emit('close')
}

async function confirm() {
    if (saving.value) return
    // Общая блокировка: пока по вакансии выполняется назначение/отклонение/сохранение, снятие не отправляется.
    if (isBusy(props.vacancy.id)) {
        error.value = 'По вакансии выполняется другое действие — дождитесь его завершения.'
        return
    }
    saving.value = true
    error.value = ''
    try {
        await guarded(props.vacancy.id, async () => {
            try {
                await exchangeApi.remove(props.vacancy.id, reason.value.trim())
            } catch (e) {
                // Запись уже удалена (404 not_found): устаревшая строка убирается обновлением списка.
                if ((e as ExchangeError)?.code !== 'not_found') throw e
                exchangeToast.error(isRequest.value ? 'Не удалось удалить запрос' : 'Не удалось снять вакансию', { text: errorText(e, ''), key: `remove:${props.vacancy.id}` })
                await props.afterRemoved(props.vacancy.id)
                return
            }
            exchangeToast.success(isRequest.value ? 'Запрос удалён' : 'Вакансия снята', { text: subject.value, key: `remove:${props.vacancy.id}` })
            await props.afterRemoved(props.vacancy.id)
        })
    } catch (e) {
        const err = e as ExchangeError
        // Причину требует только code deletion_reason_required; иной 409 (vacancy_has_shift и т. п.) — запрет с текстом.
        if (err?.code === 'deletion_reason_required') {
            serverRequired.value = true
            error.value = 'Укажите причину снятия.'
        } else {
            error.value = errorText(e, 'Не удалось снять вакансию')
        }
    } finally {
        saving.value = false
    }
}
</script>
